import { matchesSearch } from '@webbuilder/shared-types'
import type { ReactNode } from 'react'
/**
 * Ofertas: lo que el cliente vende.
 *
 * Esto es la lista. La ficha de una oferta vive en `OfferDetail` y en su propia
 * dirección, así que aquí sólo se elige a cuál entrar — y las dos acciones que
 * se usan sin entrar: ponerla a la venta y retirarla.
 *
 * Es la misma tabla que Contenido y no una lista propia: una oferta se mira
 * igual que una colección —cuál es, cuánto vale, cuándo se tocó por última vez—
 * y tener dos formas distintas de leer lo mismo obligaba a aprenderlas por
 * separado. Ordenar por columna, destacar con estrella, buscar y paginar salen
 * de ahí sin inventar nada.
 *
 * Los importes se escriben en pesos y se guardan en centavos. La conversión
 * pasa en un solo sitio (`toMinorUnits`, en `offerShared`) para que no haya dos
 * formas de entender el mismo número.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ConfirmDialog } from '../ConfirmDialog'
import { knownBrands, knownTags, withoutBrand, withoutTag } from './OfferBrandField'
import { NewOfferWizard } from './NewOfferWizard'
import { MembershipEditor } from './MembershipsSection'
import { NewOfferKindDialog } from './NewOfferKindDialog'
import { StarFilterButton } from '../StarFilterButton'
import { StarButton } from '../ListSelection'
import { usePaged, Pagination } from '../Pagination'
import { useToast } from '../../Toast'
import { readStarredIds, setStarred as setStarredIds } from '../../../lib/dashboardStars'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import {
  addOfferPrice,
  archiveOffer,
  createOffer,
  updateOffer,
  type ConnectStatus,
  type Offer,
} from '../../../lib/api'
import { AiPendingChip } from './AiPendingChip'
import { OfferStatusSelect } from './OfferStatusSelect'
import { KIND_KEY, KIND_ICON, kindOf, type OfferKind, cadenceKeyOf, STATUS_KEY, formatMoney, mainPrice, mainRevenue, publishBlockFor, revenueSummary } from './offerShared'
import { useRegion } from '../../../lib/region'
import { translateServerError, useI18n } from '../../../lib/i18n'
import type { BillingPlan } from '../../../lib/api'
import {
  IconAdjustmentsHorizontal,
  IconCalendarEvent,
  IconCheck,
  IconChevronDown,
  IconCoin,
  IconCopy,
  IconDots,
  IconEye,
  IconBriefcase,
  IconFileDescription,
  IconPackage,
  IconLayoutGrid,
  IconSchool,
  IconLoader2,
  IconPencil,
  IconPlus,
  IconSearch,
  IconShoppingCart,
  IconStar,
  IconTag,
  IconX,
} from '@tabler/icons-react'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

type StatusFilter = 'all' | 'active' | 'draft'

/**
 * Qué es cada oferta, tal y como se piensa en ella.
 *
 * No es `offer.type` a secas. Un servicio reservable es, en el modelo, una
 * oferta de tipo `service` con agenda —y así tiene que seguir siendo, porque la
 * agenda es una capacidad y no una categoría— pero quien mira esta lista no
 * busca «un servicio»: busca «una reserva». La etiqueta dice lo segundo.
 *
 * Los cuatro cajones son EXCLUYENTES a propósito: una oferta cae en uno y sólo
 * en uno, así que los filtros suman el total de la lista. Con la agenda como
 * quinto filtro aparte, un servicio reservable saldría en dos y las cuentas no
 * cuadrarían con lo que se ve.
 */
type KindFilter = 'all' | OfferKind
type OfferSort =
  | 'createdRecent' | 'createdOldest'
  | 'nameAsc' | 'nameDesc'
  | 'priceHigh' | 'priceLow'
  | 'salesHigh' | 'salesLow'
  | 'revenueHigh' | 'revenueLow'

const PAGE_SIZE = 10

function dateValue(value: string | undefined): number {
  const time = value ? new Date(value).getTime() : 0
  return Number.isFinite(time) ? time : 0
}

function formatDate(time: number): string {
  return time ? formatDashboardListDate(time) : '—'
}

export function OffersPanel({ projectId, token, connectStatus, canConnectStripe, plan, offers, failed, failureReason, refreshControl, onReload, onOpen, onOffersChange, onOpenPlans, onOpenStripeSettings, lockedKind, memberships = false, onOpenCatalog, onCreateBookingService, autoCreate = false, onAutoCreateHandled }: {
  projectId: string
  token: string
  /**
   * Sólo viaja hasta el alta, que es donde queda algo que decir sobre la
   * cuenta. `null` mientras no se sabe, y también si la consulta falló:
   * afirmar que falta conectar sin haber podido preguntar manda a reconectar
   * una cuenta que a lo mejor ya está conectada.
   */
  connectStatus: ConnectStatus | null
  /** Si el plan permite conectar Stripe. Ver `assertCanConnectStripe`. */
  canConnectStripe: boolean
  /** De paso al asistente: con él y con las publicadas se sabe si cabe una más. */
  plan: BillingPlan
  /** La lista la carga la sección: la ficha necesita la misma copia. */
  offers: Offer[]
  failed: boolean
  /** El porqué del fallo, tal cual lo contó el servidor. */
  failureReason?: string | undefined
  refreshControl?: ReactNode
  onReload: () => void
  onOpen: (offerId: string) => void
  /**
   * Deja la lista mirando sólo a un tipo, sin poder cambiarlo.
   *
   * Es lo que convierte «Productos digitales» del menú en un sitio y no en un
   * filtro que hay que volver a poner: la sección llega ya acotada y el embudo
   * no ofrece cambiarla —cambiarla sería estar en la otra sección—.
   */
  lockedKind?: OfferKind | undefined
  /**
   * Sólo membresías y paquetes de clases: la misma tabla y la misma ficha que
   * cualquier oferta (lo son), pero se crean con su propio asistente
   * (`MembershipEditor`), que además elige qué clases incluyen.
   */
  memberships?: boolean | undefined
  /** Lleva al catálogo: crear una membresía sin ninguna oferta que incluir. */
  onOpenCatalog?: (() => void) | undefined
  /**
   * Empezar una reserva, que se crea en su propia sección.
   *
   * Sin esto el selector no puede ofrecerla: el asistente de reservas necesita
   * los recursos y el horario, y eso sólo lo tiene Reservas cargado.
   */
  onCreateBookingService?: (() => void) | undefined
  /**
   * Llegar con el alta ya pedida, desde otra pantalla.
   *
   * Quien pulsa «crear» en el vacío de Ventas no quiere ver la lista: quiere el
   * alta. La misma pieza que usa el calendario de reservas con su `autoCreate`,
   * y con la misma cortesía — se avisa de que ya se atendió para que volver
   * atrás no vuelva a abrirla.
   */
  autoCreate?: boolean | undefined
  onAutoCreateHandled?: (() => void) | undefined
  onOffersChange: (update: (offers: Offer[]) => Offer[]) => void
  onOpenPlans: () => void
  onOpenStripeSettings: () => void
}) {
  // La que llevaría un precio: se enseña donde todavía no hay ninguno.
  const { currency: projectCurrency } = useRegion()
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('offers.searchPlaceholder')
  const { showToast } = useToast()
  const [createOpen, setCreateOpen] = useState(false)
  /* Con qué tipo abre el asistente. Lo fija el selector de la puerta. */
  const [createType, setCreateType] = useState<Offer['type']>('digital')
  const [chooserOpen, setChooserOpen] = useState(false)

  /**
   * Crear: primero qué, después cómo.
   *
   * Estando ya dentro de una sección de un tipo —«Productos digitales»— la
   * pregunta sobra: se sabe la respuesta por dónde se está, y volver a
   * preguntarla sería hacer contestar dos veces lo mismo.
   */
  const [membershipOpen, setMembershipOpen] = useState(false)
  const startCreate = () => {
    if (memberships) { setMembershipOpen(true); return }
    /* Las dos secciones acotadas se saltan la puerta: la respuesta a «¿qué es?»
       está en dónde se está. La reserva nunca llega aquí —su sección tiene su
       propio asistente— y el curso todavía no se puede crear. */
    if (lockedKind === 'digital' || lockedKind === 'physical' || lockedKind === 'service') {
      setCreateType(lockedKind)
      setCreateOpen(true)
      return
    }
    setChooserOpen(true)
  }

  /* Se atiende una sola vez y se avisa en el acto: si el aviso esperara a que
     se cierre el alta, volver a esta sección la abriría otra vez. */
  useEffect(() => {
    if (!autoCreate) return
    onAutoCreateHandled?.()
    startCreate()
    // `startCreate` sólo lee `lockedKind`, que no cambia en la vida de esta lista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreate, onAutoCreateHandled])

  /**
   * A dónde lleva pulsar una fila: a su ficha, sea del tipo que sea.
   *
   * Una reserva se abría en Reservas y no aquí, y eso partía la lista en dos:
   * cinco filas abrían una ficha y la sexta cambiaba de sección. Su ficha
   * existe —con sus ventas, sus analíticas y su página—, y lo que no se puede
   * editar desde ella lo dice el aviso de arriba, que además ofrece el salto a
   * Reservas para quien venía a eso. El salto sigue estando; deja de ser
   * obligatorio.
   */
  const open = (offer: Offer) => onOpen(offer.id)

  const [busyId, setBusyId] = useState<string | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [optionsTooltip, setOptionsTooltip] = useState<{ top: number; left: number } | null>(null)
  /** Posición del menú en el viewport. Se porta a `body` para que el scroll
   *  horizontal de la tabla no pueda enmascararlo. */
  const [listMenu, setListMenu] = useState<{ top: number; left: number; maxHeight: number; up: boolean }>({ top: 0, left: 0, maxHeight: 0, up: false })
  const [archiving, setArchiving] = useState<Offer | null>(null)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [kindFilter, setKindFilter] = useState<KindFilter>(lockedKind ?? 'all')
  const [sort, setSort] = useState<OfferSort>('createdRecent')
  const [filterOpen, setFilterOpen] = useState(false)
  const [starredOnly, setStarredOnly] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  /* Una venta o varias. Se escribe entero en el catálogo y no como número más
     «venta» más una «s» condicional: esa «s» es una regla del castellano, y
     encima aquí el mismo texto va al `title`, a la celda y al lector de
     pantalla, así que partirlo obligaba a rehacer el plural tres veces. */
  const salesLabel = (offer: Offer) => {
    const count = offer.salesCount ?? 0
    return count === 1 ? t('offers.sales.one') : t('offers.sales.many', { count })
  }

  /* La estrella vive en `localStorage`, como en las demás listas: no hay
     columna donde guardarla. Ver [[dashboardStars]]. */
  const [starred, setStarred] = useState<Set<string>>(() => readStarredIds(projectId, 'offers'))
  const toggleStar = (id: string) => {
    setStarred(new Set(setStarredIds(projectId, 'offers', [id], !starred.has(id))))
  }

  useEffect(() => {
    if (!menuOpenId) return
    const close = (event: PointerEvent) => {
      const target = event.target as Element | null
      if (!target?.closest?.('.dash-project-row-menu-wrap') && !target?.closest?.('.dash-offer-row-menu-portal')) setMenuOpenId(null)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [menuOpenId])

  useEffect(() => {
    if (!filterOpen) return
    const onDown = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) setFilterOpen(false)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [filterOpen])

  /* Las retiradas no se enseñan nunca: siguen existiendo por sus cobros, no
     para mirarlas. El filtro de estado reparte lo que queda.
     Y en una sección acotada («Productos físicos»…), sólo las de su tipo: si
     no, con ofertas de otro tipo la lista vacía se leía como «ninguna coincide
     con tu búsqueda» en vez de ofrecer crear la primera. */
  const live = useMemo(
    /* Las membresías y paquetes viven en su propia sección, no en «Servicios». */
    () => offers.filter((offer) => offer.status !== 'archived' && (memberships
      ? Boolean(offer.membership)
      : (!lockedKind || kindOf(offer) === lockedKind) && !(lockedKind === 'service' && offer.membership))),
    [offers, lockedKind, memberships],
  )
  /* El tope del plan cuenta las publicadas de TODOS los tipos, no las de esta
     sección: es una sola cuota para el proyecto. */
  const publishedCount = offers.filter((offer) => offer.status === 'active').length

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const rows = live.filter((offer) => {
      if (statusFilter !== 'all' && offer.status !== statusFilter) return false
      const kind = lockedKind ?? kindFilter
      if (kind !== 'all' && kindOf(offer) !== kind) return false
      if (starredOnly && !starred.has(offer.id)) return false
      if (!needle) return true
      return matchesSearch(offer.title, needle) || matchesSearch(offer.description, needle)
    })

    // Ordenar por importe compara el precio principal: es el que se enseña en
    // la fila, y ordenar por uno que no se ve deja la tabla pareciendo rota.
    const amount = (offer: Offer) => mainPrice(offer)?.unitAmount ?? -1
    /* Y por lo generado, la línea que se enseña. Comparar monedas distintas es
       comparar peras con manzanas, pero es exactamente lo que se ve en la
       columna: ordenar por una cifra oculta sería peor que ordenar por ésta. */
    const generated = (offer: Offer) => offer.revenue[0]?.amount ?? -1
    return [...rows].sort((a, b) => {
      switch (sort) {
        case 'nameAsc': return a.title.localeCompare(b.title, 'es')
        case 'nameDesc': return b.title.localeCompare(a.title, 'es')
        case 'priceHigh': return amount(b) - amount(a)
        case 'priceLow': return amount(a) - amount(b)
        case 'salesHigh': return (b.salesCount ?? 0) - (a.salesCount ?? 0)
        case 'salesLow': return (a.salesCount ?? 0) - (b.salesCount ?? 0)
        case 'revenueHigh': return generated(b) - generated(a)
        case 'revenueLow': return generated(a) - generated(b)
        case 'createdOldest': return dateValue(a.createdAt) - dateValue(b.createdAt)
        default: return dateValue(b.createdAt) - dateValue(a.createdAt)
      }
    })
  }, [live, query, statusFilter, kindFilter, lockedKind, starredOnly, starred, sort])

  const paged = usePaged(visible, PAGE_SIZE, `${query}|${statusFilter}|${kindFilter}|${sort}|${starredOnly}`)

  /** Un clic en la cabecera alterna entre las dos direcciones de esa columna. */
  const toggleSort = (column: 'name' | 'price' | 'sales' | 'revenue' | 'created') => {
    setSort((current) => {
      if (column === 'name') return current === 'nameAsc' ? 'nameDesc' : 'nameAsc'
      if (column === 'price') return current === 'priceHigh' ? 'priceLow' : 'priceHigh'
      if (column === 'sales') return current === 'salesHigh' ? 'salesLow' : 'salesHigh'
      if (column === 'revenue') return current === 'revenueHigh' ? 'revenueLow' : 'revenueHigh'
      return current === 'createdRecent' ? 'createdOldest' : 'createdRecent'
    })
  }

  /**
   * Duplicar es crear otra y copiarle los precios uno a uno.
   *
   * No hay un endpoint que lo haga de una vez, y tampoco hace falta: son las
   * mismas llamadas que haría alguien a mano. Nace en borrador a propósito —
   * una copia a la venta desde el primer segundo es una venta que nadie revisó.
   */
  /*
   * Por qué no se puede publicar cada oferta. La misma regla que la lista de
   * servicios reservables, del mismo sitio: es del servidor, y las dos pantallas
   * tienen que contarla igual.
   *
   * `connectStatus === null` es «no se sabe» —la consulta no ha vuelto o falló—
   * y ahí no se bloquea nada: el servidor sigue decidiendo.
   */
  const blockFor = (offer: Offer) => publishBlockFor(offer, {
    stripeReady: connectStatus === null ? null : connectStatus === 'ready',
    canConnectStripe,
    plan,
    /* De la lista viva: publicar una desde la tabla tiene que contar para la
       siguiente sin recargar. */
    publishedOffers: publishedCount,
  })

  const setStatus = async (offer: Offer, status: 'active' | 'draft') => {
    if (busyId) return
    setBusyId(offer.id)
    try {
      const updated = await updateOffer(projectId, offer.id, { status }, token)
      onOffersChange((rows) => rows.map((row) => (row.id === updated.id ? updated : row)))
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
    } finally {
      setBusyId(null)
    }
  }

  const duplicate = async (offer: Offer) => {
    setBusyId(offer.id)
    try {
      let copy = await createOffer(projectId, {
        title: `${offer.title} ${t('offers.copySuffix')}`.slice(0, 200),
        description: offer.description,
        images: offer.images,
        type: offer.type,
      }, token)
      for (const price of offer.prices.filter((candidate) => candidate.active)) {
        copy = await addOfferPrice(projectId, copy.id, {
          unitAmount: price.unitAmount,
          currency: price.currency,
          interval: price.interval,
          isDefault: price.isDefault,
        }, token)
      }
      const created = copy
      onOffersChange((rows) => [...rows, created])
      showToast(t('offers.duplicated'))
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
    } finally {
      setBusyId(null)
    }
  }

  const confirmArchive = async () => {
    if (!archiving) return
    try {
      await archiveOffer(projectId, archiving.id, token)
      onOffersChange((rows) => rows.filter((row) => row.id !== archiving.id))
      setArchiving(null)
      showToast(t('offers.withdrawn'))
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
    }
  }

  const filtersOn = statusFilter !== 'all' || (!lockedKind && kindFilter !== 'all') || starredOnly

  /* Con la lista vacía no sale la barra entera: ni buscador, ni filtros, ni
     el botón de crear. No hay nada que buscar ni que acotar, y crear ya lo
     ofrece el propio mensaje de empezar —que es lo único que tiene que
     haber en pantalla. Vuelve con la primera fila, o mientras quede algo
     escrito: si no, una búsqueda sin resultados se llevaría por delante la
     caja donde estaba escrita y no habría cómo deshacerla. */
  const showToolbar = live.length > 0 || query.trim() !== ''

  /* Aquí no hay espera. La lleva `PaymentsSection`, que es quien pinta el aviso
     de Stripe y el título por encima de esta lista: esperando sólo aquí, esos
     dos seguían saliendo sobre un hueco. */

  /* Fragmento y no un `div`: la barra y la tabla son hermanas dentro de la
     rejilla vertical de la pantalla —`.dash-projects-view`, con `gap: 14px`— y
     envueltas en una caja pasan a ser UN solo elemento de esa rejilla. El hueco
     entre ellas desaparecía y el buscador quedaba pegado al contenido.

     La animación de entrada la lleva la raíz de la pantalla, que es quien se
     monta al terminar la espera. */
  return (
    <>
      {!showToolbar && refreshControl ? <div className="dash-toolbar-lead">{refreshControl}</div> : null}
      {showToolbar ? (
        <header className="dash-toolbar dash-layered-toolbar">
          <div className="dash-toolbar-lead">
            {refreshControl}
          <div className="dash-toolbar-search">
            <button type="button" className="dash-toolbar-search-icon" aria-label={t('offers.searchAria')} onClick={() => searchRef.current?.focus()}>
              <IconSearch size={16} />
            </button>
            <input
              ref={searchRef}
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Escape') { setQuery(''); searchRef.current?.blur() } }}
            />
            {query ? (
              <button type="button" className="dash-toolbar-search-clear" aria-label={t('offers.clearSearch')} onClick={() => { setQuery(''); searchRef.current?.focus() }}>
                <IconX size={13} />
              </button>
            ) : null}
          </div>

          </div>

          <div className="dash-toolbar-controls">
            <StarFilterButton active={starredOnly} onToggle={() => setStarredOnly((only) => !only)} label={t('offers.starLabel')} />
            <div className="dash-filter-wrap" ref={filterRef}>
              <button
                className="dash-filter-trigger"
                type="button"
                aria-label={t('offers.filtersAria')}
                data-tooltip={t('offers.filtersAria')}
                aria-expanded={filterOpen}
                onClick={() => setFilterOpen((open) => !open)}
              >
                <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                {filtersOn ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
              </button>
              {filterOpen ? (
                <div className="dash-filter-popover" role="dialog" aria-label={t('offers.filtersDialogAria')}>
                  <div className="dash-filter-popover-head">
                    <strong>{t('offers.filterTitle')}</strong>
                    <button type="button" aria-label={t('offers.closeFilters')} onClick={() => setFilterOpen(false)}><IconX size={15} /></button>
                  </div>
                  {/* «Qué es» va antes que «en qué estado»: es la pregunta más
                      gruesa de las dos —primero se acota el catálogo, después
                      se mira si está publicado— y ordenarlas al revés obligaba
                      a bajar y volver a subir. */}
                  {lockedKind ? null : (
                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('offers.filterKind')}</span>
                      <div className="dash-filter-options">
                        {([
                          ['all', 'offers.filter.all', IconLayoutGrid],
                          ['digital', KIND_KEY.digital, IconFileDescription],
                          ['physical', KIND_KEY.physical, IconPackage],
                          ['booking', KIND_KEY.booking, IconCalendarEvent],
                          ['course', KIND_KEY.course, IconSchool],
                          ['service', KIND_KEY.service, IconBriefcase],
                        ] as const).map(([value, labelKey, Icon]) => (
                          <button key={value} className={kindFilter === value ? 'active' : ''} type="button" onClick={() => setKindFilter(value)}>
                            <Icon size={14} />
                            <span>{t(labelKey)}</span>
                            {kindFilter === value ? <IconCheck size={14} /> : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('offers.filterStatus')}</span>
                    <div className="dash-filter-options">
                      {([
                        ['all', 'offers.filter.all', IconLayoutGrid],
                        ['active', STATUS_KEY.active, IconEye],
                        ['draft', STATUS_KEY.draft, IconPencil],
                      ] as const).map(([value, labelKey, Icon]) => (
                        <button key={value} className={statusFilter === value ? 'active' : ''} type="button" onClick={() => setStatusFilter(value)}>
                          <Icon size={14} />
                          <span>{t(labelKey)}</span>
                          {statusFilter === value ? <IconCheck size={14} /> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('offers.filterStarred')}</span>
                    <div className="dash-filter-options">
                      <button className={!starredOnly ? 'active' : ''} type="button" onClick={() => setStarredOnly(false)}><IconLayoutGrid size={14} /><span>{t('offers.allStars')}</span>{!starredOnly ? <IconCheck size={14} /> : null}</button>
                      <button className={starredOnly ? 'active' : ''} type="button" onClick={() => setStarredOnly(true)}><IconStar size={14} /><span>{t('offers.onlyStarred')}</span>{starredOnly ? <IconCheck size={14} /> : null}</button>
                    </div>
                  </div>
                  {filtersOn ? (
                    <button className="dash-filter-reset" type="button" onClick={() => { setStatusFilter('all'); setKindFilter('all'); setStarredOnly(false) }}>
                      {t('offers.resetFilters')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <button
              className="dash-primary-btn dash-toolbar-create"
              type="button"
              aria-label={t(memberships ? 'memberships.new' : 'offers.new')}
              onClick={startCreate}
            >
              <IconPlus size={15} />
              <span className="dash-btn-label dash-btn-label--desktop">{t(memberships ? 'memberships.new' : 'offers.new')}</span>
              <span className="dash-btn-label dash-btn-label--compact">{t('offers.newShort')}</span>
            </button>
          </div>
        </header>
      ) : null}

      {/* Aquí iba un recordatorio de que faltaba conectar Stripe. Lo decía el
          banner de arriba con las mismas palabras, y repetir el mismo aviso dos
          veces en una pantalla lo convierte en insistencia, no en ayuda. */}

      {live.length > 0 ? (
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('offers.unit')} />
      ) : null}

      {failed ? (
        <div className="dash-inline-msg">
          {/* El detalle va aquí y no sólo en la consola: quien ve esto es quien
              puede decir qué estaba haciendo, y sin el motivo delante la única
              salida era ponerse a adivinar. */}
          <span>
            {t('offers.loadFailed')}
            {failureReason ? <code className="dash-failure-detail">{failureReason}</code> : null}
          </span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={onReload}>{t('offers.retry')}</button>
          </div>
        </div>
      ) : live.length === 0 ? (
        /* El mismo dibujo que los vacíos del CMS del editor: un trazo suelto,
           un titular y una frase. Sin la pastilla azul de 46px que llevaba
           antes — con la tabla vacía el hueco es toda la pantalla, y ahí un
           icono con fondo de color pesa más que lo que tiene que decir. */
        <div className="dash-placeholder">
          <IconCoin size={34} aria-hidden="true" />
          <strong>{t(memberships ? 'memberships.emptyTitle' : 'offers.empty.title')}</strong>
          {/* Nada de planes aquí. Crear es gratis: lo que cuesta es cobrar, y
              eso ya lo dice el aviso de Stripe de arriba. Repetirlo en el hueco
              de empezar convertía la primera pantalla en un peaje. */}
          <span>{t(memberships ? 'memberships.emptyBody' : 'offers.empty.body')}</span>
          <button type="button" className="dash-primary-btn" onClick={startCreate}>
            <IconPlus size={14} /> {t(memberships ? 'memberships.new' : 'offers.new')}
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="dash-placeholder">
          <IconSearch size={34} aria-hidden="true" />
          <strong>{t('offers.noResults.title')}</strong>
          <span>{t('offers.noResults.body')}</span>
          <button type="button" className="dash-secondary-btn" onClick={() => { setQuery(''); setStatusFilter('all'); setStarredOnly(false) }}>
            {t('offers.clearFilters')}
          </button>
        </div>
      ) : (
        <>
        <div className="dash-cms-table-scroll">
        <div className="dash-data-table dash-offers-data-table has-stars-only">
          <div className="dash-data-header" role="row">
            <span className="dash-data-select-cell" />
            <button className={sort === 'nameAsc' || sort === 'nameDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('name')}>
              {t('offers.col.offer')} <IconChevronDown className={sort === 'nameAsc' ? 'is-asc' : ''} size={13} />
            </button>
            <button className={sort === 'priceHigh' || sort === 'priceLow' ? 'is-active' : ''} type="button" onClick={() => toggleSort('price')}>
              {t('offers.col.price')} <IconChevronDown className={sort === 'priceLow' ? 'is-asc' : ''} size={13} />
            </button>
            <span>{t('offers.col.status')}</span>
            <button className={sort === 'salesHigh' || sort === 'salesLow' ? 'is-active' : ''} type="button" onClick={() => toggleSort('sales')}>
              {t('offers.col.sales')} <IconChevronDown className={sort === 'salesLow' ? 'is-asc' : ''} size={13} />
            </button>
            {/* «Generado» y no «Ingresos»: es lo que ha entrado por esta oferta
                desde que existe, no un ingreso del mes ni una previsión. La
                columna va pegada a Ventas porque las dos responden a la misma
                pregunta —cuánto ha vendido esto— una en compras y otra en
                dinero, y separadas obligaban a cruzar la tabla para juntarlas. */}
            <button className={sort === 'revenueHigh' || sort === 'revenueLow' ? 'is-active' : ''} type="button" onClick={() => toggleSort('revenue')}>
              {t('offers.col.revenue')} <IconChevronDown className={sort === 'revenueLow' ? 'is-asc' : ''} size={13} />
            </button>
            <button className={sort === 'createdRecent' || sort === 'createdOldest' ? 'is-active' : ''} type="button" onClick={() => toggleSort('created')}>
              {t('offers.col.created')} <IconChevronDown className={sort === 'createdOldest' ? 'is-asc' : ''} size={13} />
            </button>
            <span className="dash-data-header-actions">{t('offers.col.actions')}</span>
          </div>

          <div className="dash-project-list">
            {paged.pageItems.map((offer) => {
              const price = mainPrice(offer)
              const revenue = mainRevenue(offer)
              const created = dateValue(offer.createdAt)
              const kind = kindOf(offer)
              /* Una membresía se dice como tal: cobro recurrente = membresía,
                 pago único = paquete de clases. */
              const membershipKind = offer.membership ? (price && price.interval !== 'one_time' ? 'membership' : 'pack') : null
              const KindIcon = membershipKind ? IconStar : KIND_ICON[kind]
              return (
                <article
                  className="dash-project-row dash-data-row dash-loaded-content-in"
                  key={offer.id}
                  /* Toda la ficha abre, y el toque se recoge aquí y no sólo en el
                     bloque del nombre: en el teléfono un toque en un hueco de la
                     zona sensible —el relleno, los datos— no abría nada. Los mandos
                     de dentro (estrella, estado, menú) siguen siendo suyos. */
                  onClick={openOnRowClick(() => open(offer))}
                >
                  {/* Sin casilla de selección —las ofertas no se retiran en
                      bloque—, así que la celda lleva sólo la estrella. */}
                  <span className="dash-data-select-cell">
                    <StarButton
                      starred={starred.has(offer.id)}
                      label={t('offers.starAria', { title: offer.title })}
                      onToggle={() => toggleStar(offer.id)}
                    />
                  </span>

                  <div
                    className="dash-project-row-main"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => { if (event.key === 'Enter') open(offer) }}
                  >
                    {offer.images[0] ? (
                      <span className="dash-project-row-thumb dash-page-project-thumb dash-offer-row-thumb">
                        <img src={offer.images[0]} alt="" loading="lazy" />
                      </span>
                    ) : (
                      <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback dash-offer-row-thumb">
                        <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true"><IconTag size={13} /></span>
                      </span>
                    )}
                    <span className="dash-project-row-copy">
                      <span className="dash-project-row-title">
                        <strong>{offer.title}</strong>
                        <span className={`dash-offer-status dash-offer-status-compact is-${offer.status}`}>
                          {t(STATUS_KEY[offer.status])}
                        </span>
                      </span>
                      {/* El tipo bajo el título, con su icono: igual que el
                          widget del catálogo en Inicio. */}
                      <span className="dash-project-row-details dash-offer-row-kind">
                        <KindIcon size={13} aria-hidden="true" />
                        {t(membershipKind ? `memberships.kind.${membershipKind}` : KIND_KEY[kind])}
                      </span>
                    </span>
                  </div>

                  <div className="dash-list-row-meta dash-offer-row-meta">
                    <span className="dash-data-cell dash-offer-price-cell" data-label={t('offers.col.price')}>
                      {price ? (
                        <>
                          <strong>{price.formatted}</strong>
                          <small>{t(cadenceKeyOf(price))}</small>
                        </>
                      ) : <em>{t('offers.noPrice')}</em>}
                    </span>
                    <span className="dash-data-cell dash-offer-status-cell" data-label={t('offers.col.status')}>
                      {/* Selector si se puede cambiar, etiqueta si no. Publicar
                          obligaba a entrar a la ficha, y es lo que más se repite
                          mientras se prepara una oferta. Retirada no se elige
                          desde aquí: eso es el «Retirar» del menú, que pregunta
                          antes porque no se deshace igual. */}
                      {offer.setupPending ? (
                        <AiPendingChip compact onComplete={() => open(offer)} />
                      ) : offer.status === 'archived' ? (
                        <span className={`dash-offer-status is-${offer.status}`}>{t(STATUS_KEY[offer.status])}</span>
                      ) : (
                        <OfferStatusSelect
                          compact
                          status={offer.status}
                          busy={busyId === offer.id}
                          blocked={blockFor(offer)}
                          itemType={offer.type === 'digital' ? 'digital' : 'service'}
                          label={offer.title}
                          onSelect={(status) => { void setStatus(offer, status) }}
                          onConnect={onOpenStripeSettings}
                          onOpenPlans={onOpenPlans}
                        />
                      )}
                    </span>
                    {/* Ahora sí hay cobros que contar, así que el cero es un
                        cero de verdad: nadie ha comprado esto todavía.

                        Fuera de escritorio no hay encabezados de columna, y ahí
                        un número suelto no dice de qué es: el carrito se enseña
                        —en la tabla sobra, porque arriba pone «Ventas»— y la
                        palabra viaja en el título y para el lector de pantalla,
                        que si no leería «16» sin más. */}
                    <span
                      className="dash-data-cell dash-offer-sales-cell"
                      data-label={t('offers.col.sales')}
                      title={salesLabel(offer)}
                    >
                      <IconShoppingCart className="dash-list-meta-icon" size={14} aria-hidden="true" />
                      <span aria-hidden="true">{(offer.salesCount ?? 0) > 0 ? offer.salesCount : <em>0</em>}</span>
                      <span className="dash-sr-only">
                        {salesLabel(offer)}
                      </span>
                    </span>
                    {/* Lo generado, neto de reembolsos. Sin ventas se escribe un
                        cero apagado como el del carrito y no un guion: la
                        pregunta tiene respuesta —no ha generado nada— y un
                        guion la deja pareciendo un dato que falta.

                        Con dos monedas sólo cabe la que más pesa; el resto se
                        anuncia con un «+1» y va entero en el título, que es
                        también lo que hacen las analíticas de venta con su
                        gráfica. Sumarlas en un número sería una cifra falsa. */}
                    <span
                      className="dash-data-cell dash-offer-revenue-cell"
                      data-label={t('offers.col.revenue')}
                      title={revenueSummary(offer, t)}
                    >
                      {revenue ? (
                        <>
                          <strong>{revenue.line.formatted}</strong>
                          {revenue.rest.length > 0 ? <small>+{revenue.rest.length}</small> : null}
                        </>
                      ) : <em>{formatMoney(0, mainPrice(offer)?.currency ?? projectCurrency)}</em>}
                    </span>
                    <time className="dash-data-cell dash-list-date-cell" data-label={t('offers.col.created')} dateTime={created ? new Date(created).toISOString() : undefined}>
                      <IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatDate(created)}
                    </time>
                  </div>

                  <div className="dash-project-row-actions">
                    {/* Entrar, no publicar. Poner algo a la venta desde una
                        fila es decidir a ciegas: no ves el precio completo, ni
                        si tiene imagen, ni si la descripción está a medias.
                        Publicar vive dentro de la ficha, con todo delante. */}
                    <button
                      className="dash-secondary-btn dash-project-row-action dash-open-content-action"
                      type="button"
                      onClick={() => open(offer)}
                    >
                      <IconEye size={14} />
                      {/* Sin variante compacta: «Abrir» ya cabe en la fila
                          estrecha, así que no hay nada que acortar. */}
                      <span className="dash-project-row-action-label">{t('offers.open')}</span>
                    </button>

                    <div className={`dash-project-row-menu-wrap${menuOpenId === offer.id ? ' is-open' : ''}`}>
                      <button
                        className="project-menu-btn dash-icon-btn dash-page-options-btn"
                        type="button"
                        aria-label={t('offers.moreOptions')}
                        aria-expanded={menuOpenId === offer.id}
                        disabled={busyId === offer.id}
                        onPointerEnter={(event) => {
                          if (menuOpenId === offer.id) return
                          const rect = event.currentTarget.getBoundingClientRect()
                          const tooltipWidth = 70
                          const edge = 12
                          const center = rect.left + rect.width / 2
                          const left = Math.max(edge + tooltipWidth / 2, Math.min(window.innerWidth - edge - tooltipWidth / 2, center))
                          setOptionsTooltip({ top: rect.top - 8, left })
                        }}
                        onPointerLeave={() => setOptionsTooltip(null)}
                        onFocus={(event) => {
                          if (menuOpenId === offer.id) return
                          const rect = event.currentTarget.getBoundingClientRect()
                          const tooltipWidth = 70
                          const edge = 12
                          const center = rect.left + rect.width / 2
                          const left = Math.max(edge + tooltipWidth / 2, Math.min(window.innerWidth - edge - tooltipWidth / 2, center))
                          setOptionsTooltip({ top: rect.top - 8, left })
                        }}
                        onBlur={() => setOptionsTooltip(null)}
                        onClick={(event) => {
                          event.stopPropagation()
                          setOptionsTooltip(null)
                          if (menuOpenId !== offer.id) {
                            const rect = event.currentTarget.getBoundingClientRect()
                            const menuWidth = 184
                            const menuHeight = 114
                            const edge = 12
                            const gap = 8
                            const spaceBelow = window.innerHeight - rect.bottom - edge
                            const spaceAbove = rect.top - edge
                            const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
                            const top = up
                              ? Math.max(edge, rect.top - menuHeight - gap)
                              : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
                            const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
                            setListMenu({ top, left, up, maxHeight: Math.max(menuHeight, up ? spaceAbove - gap : spaceBelow - gap) })
                          }
                          setMenuOpenId(menuOpenId === offer.id ? null : offer.id)
                        }}
                      >
                        {/* Duplicar tarda —son varias llamadas seguidas— y sin
                            señal parecía que el menú no había hecho nada. */}
                        {busyId === offer.id ? <IconLoader2 className="dash-spin" size={16} /> : <IconDots size={16} />}
                      </button>
                      {menuOpenId === offer.id ? createPortal(
                        <div
                          className={`project-menu dash-project-row-menu dash-offer-row-menu-portal${listMenu.up ? ' is-up' : ''}`}
                          role="menu"
                          style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}
                        >
                          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); open(offer) }}>
                            <IconPencil size={14} /> {t('offers.menu.open')}
                          </button>
                          <button className="project-menu-item" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); void duplicate(offer) }}>
                            <IconCopy size={14} /> {t('offers.menu.duplicate')}
                          </button>
                          <button className="project-menu-item danger" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); setArchiving(offer) }}>
                            <IconX size={14} /> {t('offers.menu.withdraw')}
                          </button>
                        </div>,
                        document.body,
                      ) : null}
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </div>
        </div>
        {/* Y al pie: quien acaba de recorrer la página busca el siguiente paso
            donde terminó de leer. Con una sola, sólo queda la cuenta. */}
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('offers.unit')} alwaysShow />
        </>
      )}

      {optionsTooltip ? createPortal(
        <span
          className="dash-portal-tooltip"
          role="tooltip"
          style={{ top: optionsTooltip.top, left: optionsTooltip.left }}
        >
          {t('offers.options')}
        </span>,
        document.body,
      ) : null}

      {chooserOpen ? (
        <NewOfferKindDialog
          onClose={() => setChooserOpen(false)}
          onPick={(kind) => {
            setChooserOpen(false)
            /* La reserva no se crea aquí: su asistente necesita los recursos y
               las reglas del horario, que sólo carga su sección. Se va allí con
               la orden de abrirlo — el mismo camino que ya usa el calendario. */
            if (kind === 'booking') { onCreateBookingService?.(); return }
            setCreateType(kind)
            setCreateOpen(true)
          }}
        />
      ) : null}

      {createOpen ? (
        <NewOfferWizard
          initialType={createType}
          projectId={projectId}
          token={token}
          /* El estado de Stripe lo consulta el propio asistente —`useStripeConnect`—
             porque además de leerlo ofrece conectar y espera a que termine. El
             `connectStatus` de esta lista sigue siendo el de la lista. */
          canConnectStripe={canConnectStripe}
          plan={plan}
          /* De la lista viva, que es la que acaba de cambiar: publicar una y
             volver a crear otra tiene que contar la primera. */
          publishedOffers={publishedCount}
          brands={knownBrands(offers)}
          onBrandRemoved={(name) => onOffersChange((rows) => withoutBrand(rows, name))}
          tags={knownTags(offers)}
          onTagRemoved={(name) => onOffersChange((rows) => withoutTag(rows, name))}
          onOpenPlans={onOpenPlans}
          onClose={() => setCreateOpen(false)}
          onCreated={(offer) => {
            onOffersChange((rows) => [...rows, offer])
            setCreateOpen(false)
            // Recién creada sólo tiene nombre y precio: lo siguiente que quiere
            // quien la crea es rellenar el resto, y eso está en la ficha.
            onOpen(offer.id)
          }}
        />
      ) : null}

      {membershipOpen ? (
        <MembershipEditor
          projectId={projectId}
          token={token}
          offerId={null}
          onOpenPlans={onOpenPlans}
          {...(onOpenCatalog ? { onOpenCatalog } : {})}
          onClose={() => setMembershipOpen(false)}
          onSaved={(offerId) => {
            setMembershipOpen(false)
            onReload()
            onOpen(offerId)
          }}
        />
      ) : null}

      {archiving ? (
        <ConfirmDialog
          title={t('offers.withdraw.title')}
          message={t('offers.withdraw.body')}
          confirmLabel={t('offers.withdraw.confirm')}
          tone="danger"
          onCancel={() => setArchiving(null)}
          onConfirm={confirmArchive}
        />
      ) : null}
    </>
  )
}
